import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, Loader2, Plus, Send, FlaskConical, Users, ShieldCheck, ExternalLink, Trash2 } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600", queued: "bg-amber-100 text-amber-800", scheduled: "bg-amber-100 text-amber-800",
  sent: "bg-emerald-100 text-emerald-800", inProcess: "bg-blue-100 text-blue-800", suspended: "bg-red-100 text-red-800",
};

function pct(v) { return v === null || v === undefined ? "—" : `${v}%`; }

function LaunchDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ name: "", subject: "", template_id: "", sender_email: "" });
  const [listIds, setListIds] = useState([]);
  const [bid, setBid] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testSent, setTestSent] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: templates = [] } = useQuery({
    queryKey: ["brevo-templates"],
    queryFn: async () => (await api.get("/marketing/brevo/templates")).data,
    enabled: open,
  });
  const { data: overview } = useQuery({
    queryKey: ["brevo-overview"],
    queryFn: async () => (await api.get("/marketing/brevo/overview")).data,
    enabled: open,
  });
  const { data: senders } = useQuery({
    queryKey: ["brevo-senders"],
    queryFn: async () => (await api.get("/marketing/senders")).data,
    enabled: open, retry: false,
  });

  const lists = overview?.lists || [];
  const chosen = lists.filter((l) => listIds.includes(l.id));
  const totalRecipients = chosen.reduce((s, l) => s + (l.unique || l.total || 0), 0);
  const tplName = templates.find((t) => t.id === Number(f.template_id))?.name || "";

  const reset = () => { setStep(1); setF({ name: "", subject: "", template_id: "", sender_email: "" }); setListIds([]); setBid(null); setTestEmail(""); setTestSent(false); setScheduleAt(""); };

  const createDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/brevo/campaigns", {
        name: f.name.trim(), subject: f.subject.trim(), template_id: Number(f.template_id),
        list_ids: listIds, sender_email: f.sender_email || undefined,
      });
      setBid(data.brevo_id);
      setStep(4);
      toast.success("Draft created in Brevo — send a test before launching");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter your email for the test"); return; }
    setBusy(true);
    try {
      await api.post(`/marketing/brevo/campaigns/${bid}/test`, { email: testEmail.trim() });
      setTestSent(true);
      toast.success(`Test sent to ${testEmail.trim()} — check your inbox before launching`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const launch = async (schedule) => {
    setBusy(true);
    try {
      const body = schedule && scheduleAt ? { scheduled_at: new Date(scheduleAt).toISOString() } : {};
      const { data } = await api.post(`/marketing/brevo/campaigns/${bid}/send`, body);
      toast.success(data.status === "scheduled" ? "Campaign scheduled" : "Campaign is sending now");
      onOpenChange(false); reset(); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="brevo-launch-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New Brevo Campaign — step {step} of 4</DialogTitle>
          <DialogDescription>
            {step === 1 && "Basics: name, subject, Brevo template and sender."}
            {step === 2 && "Pick the Brevo list(s) that will receive this campaign."}
            {step === 3 && "Confirm everything before the draft is created in Brevo."}
            {step === 4 && "Test it on yourself, then launch or schedule."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Campaign name *</Label>
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. August Product Update" data-testid="brevo-camp-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email subject *</Label>
              <Input value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="brevo-camp-subject" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Brevo template *</Label>
              <Select value={f.template_id ? String(f.template_id) : ""} onValueChange={(v) => set("template_id", v)}>
                <SelectTrigger className="h-9" data-testid="brevo-camp-template"><SelectValue placeholder={templates.length ? "Pick a Brevo template" : "No active templates in Brevo"} /></SelectTrigger>
                <SelectContent className="max-h-56">{templates.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              {templates.length === 0 && <p className="text-[11px] text-amber-600">No active templates found in your Brevo account — create one in Brevo, or use a CRM Campaign instead.</p>}</div>
            <div className="space-y-1.5"><Label className="text-xs">Sender</Label>
              <Select value={f.sender_email || "__auto"} onValueChange={(v) => set("sender_email", v === "__auto" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="brevo-camp-sender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto">Automatic (verified Brevo sender)</SelectItem>
                  {(senders?.senders || []).filter((s) => s.active).map((s) => <SelectItem key={s.email} value={s.email}>{s.name} — {s.email}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2">
            {lists.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No Brevo lists — create one in the Brevo Hub first.</p>}
            {lists.map((l) => (
              <label key={l.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer ${listIds.includes(l.id) ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`brevo-list-option-${l.id}`}>
                <input type="checkbox" checked={listIds.includes(l.id)}
                  onChange={() => setListIds((p) => p.includes(l.id) ? p.filter((x) => x !== l.id) : [...p, l.id])} />
                <span className="text-sm font-medium text-slate-800 flex-1">{l.name}</span>
                <span className="text-xs text-slate-400 font-mono">{l.unique || l.total} contacts</span>
              </label>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 text-sm" data-testid="brevo-confirm-panel">
            <Card className="p-3.5 space-y-1.5 bg-slate-50">
              <p><span className="text-slate-400 text-xs uppercase tracking-wide mr-2">Campaign</span><span className="font-semibold">{f.name}</span></p>
              <p><span className="text-slate-400 text-xs uppercase tracking-wide mr-2">Subject</span>{f.subject}</p>
              <p><span className="text-slate-400 text-xs uppercase tracking-wide mr-2">Template</span>{tplName}</p>
              <p><span className="text-slate-400 text-xs uppercase tracking-wide mr-2">Audience</span>{chosen.map((l) => l.name).join(", ")}</p>
              <p className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-slate-500" /><span className="font-bold font-mono" data-testid="brevo-confirm-count">{totalRecipients}</span> contacts in the selected list(s)</p>
            </Card>
            <p className="text-[11px] text-emerald-700 flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Opted-out / bounced addresses are synced to the Brevo blocklist and automatically excluded at send time — even if they are in these lists.</p>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <Card className="p-3.5 bg-amber-50/60 border-amber-200">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-800 flex items-center gap-1.5 mb-2"><FlaskConical className="h-3.5 w-3.5" /> Test first (required)</p>
              <div className="flex gap-1.5">
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your.email@united-group.in" className="h-9 bg-white" data-testid="brevo-test-email" />
                <Button onClick={sendTest} disabled={busy} variant="outline" className="h-9 shrink-0 bg-white" data-testid="brevo-test-send-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send test"}</Button>
              </div>
              <p className="text-[11px] text-amber-700 mt-1.5">The test address must exist as a contact in Brevo. {testSent && "✓ Test sent — check your inbox."}</p>
            </Card>
            <div className="space-y-1.5"><Label className="text-xs">Schedule for later (optional)</Label>
              <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="h-9" data-testid="brevo-schedule-input" /></div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && step < 4 && <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>}
          {step === 1 && <Button className="bg-slate-900 hover:bg-slate-800" data-testid="brevo-step1-next" onClick={() => {
            if (!f.name.trim() || !f.subject.trim() || !f.template_id) { toast.error("Name, subject and template are required"); return; }
            setStep(2);
          }}>Next — Audience</Button>}
          {step === 2 && <Button className="bg-slate-900 hover:bg-slate-800" data-testid="brevo-step2-next" onClick={() => {
            if (!listIds.length) { toast.error("Pick at least one list"); return; }
            setStep(3);
          }}>Next — Confirm</Button>}
          {step === 3 && <Button className="bg-slate-900 hover:bg-slate-800" onClick={createDraft} disabled={busy} data-testid="brevo-create-draft-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Create Draft in Brevo</Button>}
          {step === 4 && (
            <>
              {scheduleAt ? (
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => launch(true)} disabled={busy || !testSent} data-testid="brevo-schedule-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Schedule</Button>
              ) : (
                <Button className="bg-[#f04935] hover:bg-[#d63c2a]" onClick={() => launch(false)} disabled={busy || !testSent} data-testid="brevo-send-now-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}<Send className="h-4 w-4 mr-1" />Send Now</Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BrevoCampaigns() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [launchOpen, setLaunchOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["brevo-campaigns"],
    queryFn: async () => (await api.get("/marketing/brevo/campaigns")).data,
  });
  const { data: status } = useQuery({
    queryKey: ["brevo-status-mini"],
    queryFn: async () => (await api.get("/marketing/brevo/status")).data,
  });
  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.get("/marketing/brevo/campaigns", { params: { refresh: 1 } });
      qc.invalidateQueries({ queryKey: ["brevo-campaigns"] });
      toast.success("Stats refreshed from Brevo");
    } catch (e) { toast.error(apiError(e)); } finally { setRefreshing(false); }
  };
  const del = async (bid) => {
    if (!window.confirm("Delete this draft campaign in Brevo?")) return;
    try {
      await api.delete(`/marketing/brevo/campaigns/${bid}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["brevo-campaigns"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const rows = data?.rows || [];
  return (
    <div data-testid="brevo-campaigns-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-xs text-slate-400" data-testid="brevo-account-caption">
          Pulled from Brevo account{status?.account?.email ? <b className="text-slate-600"> {status.account.email}{status.account.company ? ` (${status.account.company})` : ""}</b> : status?.error ? <span className="text-red-500"> — connection error: {status.error}</span> : ""}
          {data?.last_refresh ? ` · last refresh ${formatDate(data.last_refresh)}` : ""} — no Brevo login needed.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={refresh} disabled={refreshing} data-testid="brevo-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh stats</Button>
          <Button size="sm" className="h-8 gap-1.5 bg-slate-900 hover:bg-slate-800" onClick={() => setLaunchOpen(true)} data-testid="brevo-new-campaign-btn">
            <Plus className="h-3.5 w-3.5" /> New Brevo Campaign</Button>
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Campaign", "Status", "Sent", "Delivered", "Opens", "Open %", "Clicks", "Click %", "Bounces", "Unsubs", "Date", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.brevo_id} className="cursor-pointer" onClick={() => navigate(`/marketing/brevo-campaign/${c.brevo_id}`)} data-testid={`brevo-campaign-row-${c.brevo_id}`}>
                    <TableCell className="font-medium">{c.name}{c.created_from_crm && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 font-bold uppercase">CRM</span>}</TableCell>
                    <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status}</span></TableCell>
                    <TableCell className="font-mono text-sm">{c.stats?.sent ?? 0}</TableCell>
                    <TableCell className="font-mono text-sm">{c.stats?.delivered ?? 0}</TableCell>
                    <TableCell className="font-mono text-sm">{c.stats?.unique_opens ?? 0}</TableCell>
                    <TableCell className="font-mono text-sm">{pct(c.stats?.open_rate)}</TableCell>
                    <TableCell className="font-mono text-sm">{c.stats?.unique_clicks ?? 0}</TableCell>
                    <TableCell className="font-mono text-sm">{pct(c.stats?.click_rate)}</TableCell>
                    <TableCell className="font-mono text-sm">{(c.stats?.hard_bounces || 0) + (c.stats?.soft_bounces || 0)}</TableCell>
                    <TableCell className="font-mono text-sm">{c.stats?.unsubscribes ?? 0}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{c.sent_date ? formatDate(c.sent_date) : c.scheduled_at ? `⏱ ${formatDate(c.scheduled_at)}` : "—"}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {c.status === "draft" && (
                        <button onClick={() => del(c.brevo_id)} className="text-slate-300 hover:text-red-600 p-1" title="Delete draft" data-testid={`brevo-delete-${c.brevo_id}`}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={12} className="text-center py-12 text-slate-400"><ExternalLink className="h-6 w-6 mx-auto mb-2 text-slate-300" />No Brevo campaigns found — hit Refresh, or create one.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
      <LaunchDialog open={launchOpen} onOpenChange={setLaunchOpen} onDone={() => qc.invalidateQueries({ queryKey: ["brevo-campaigns"] })} />
    </div>
  );
}
